import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isCorrectAnswer } from '../utils/answer';
import '../styles/InputQuizModal.css';

export default function InputQuizModal({ isOpen, onClose, quiz, onShowResult }) {
  const { t } = useTranslation();

  // ---- state ----
  const [inputValue, setInputValue] = useState('');
  const isCompleted = !!quiz?.completed;
  const correctAnswer = quiz?.answer ?? '';

  // 正解の見かけ文字数に追従（フォールバック 6）
  const expectedLen = useMemo(() => {
    if (!correctAnswer) return 6;
    return [...String(correctAnswer)].length;
  }, [correctAnswer]);

  const displayChars = useMemo(() => [...inputValue], [inputValue]);

  // ---- handlers ----
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleKeypadClick = (char) => {
    if (isCompleted || !quiz?.isNearby) return;
    setInputValue((prev) => {
      const next = prev + char;
      return [...next].length > expectedLen ? prev : next;
    });
  };

  const handleSubmit = () => {
    if (isCompleted || !quiz?.isNearby) return;
    const ok = isCorrectAnswer(inputValue, correctAnswer);
    onShowResult?.(ok);
    setInputValue('');
  };

  const clearInput = () => setInputValue('');

  // ---- assets ----
  const BADGE_SRC = isCompleted
    ? '/images/stamp/quizget-icon.svg'
    : '/images/stamp/quiz-icon.svg';

  const thumbnailSrc = quiz?.thumbnail || quiz?.image || '';

  // ---- render ----
  return (
    <>
      {isOpen && quiz ? (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="input-quiz-modal">
            <button className="close-btn" onClick={onClose}>✕</button>

            {/* header */}
            <div className="quiz-header">
              {/* バッジ画像（SVG） */}
              <img
                src={BADGE_SRC}
                alt=""
                aria-hidden
                className="quiz-spot-badge-img"
                onError={(e) => {
                  // 画像エラー時は未獲得アイコンにフォールバック
                  e.currentTarget.src = '/images/stamp/quiz-icon.svg';
                }}
              />
              <h2 className="quiz-title">
                {quiz.title}
                {isCompleted && (
                  <span className="quiz-completed-badge">
                    <span aria-hidden>✅</span> {t('quiz.completed')}
                  </span>
                )}
              </h2>
            </div>

            {/* サムネイル（ホットケーキの位置） */}
            {thumbnailSrc && (
              <div className="quiz-illustration-wrap" aria-hidden>
                <img
                  src={thumbnailSrc}
                  alt=""
                  className="quiz-illustration"
                  onError={(e) => {
                    // サムネイルのフォールバックは非表示に
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* 質問文 */}
            <p className="quiz-question">{quiz.question}</p>

            {/*
            {quiz.onRouteSearch && (
              <button
                onClick={quiz.onRouteSearch}
                disabled={quiz.routeLoading}
                style={{
                  marginTop: '12px',
                  padding: '10px',
                  width: '100%',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#ff4d4f',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: quiz.routeLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {quiz.routeLoading ? '経路検索中...' : 'ここまでの経路'}
              </button>
            )}
            */}

            {/* 完了/未完了 表示切り替え */}
            {isCompleted ? (
              <div className="input-completed-view">
                <div className="input-completed-txt">{t('quiz.completed')}</div>
                {correctAnswer && (
                  <div className="input-correct-answer">
                    {t('quiz.correctAnswer')} <strong>{correctAnswer}</strong>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* 入力表示（□ □ □ …） */}
                <div className="input-display">
                  {Array.from({ length: expectedLen }, (_, i) => (
                    <div key={i} className="input-box">
                      {displayChars[i] || ''}
                    </div>
                  ))}
                </div>

                {!quiz.isNearby ? (
                  <div className="quiz-locked">{t('quiz.moveCloser')}</div>
                ) : (
                  <div className="keypad">
                    {quiz.keypad?.map((row, rowIndex) => (
                      <div key={rowIndex} className="keypad-row">
                        {row.map((char, colIndex) => (
                          <button
                            key={`${rowIndex}-${colIndex}`}
                            className="keypad-btn"
                            onClick={() => handleKeypadClick(char)}
                          >
                            {char}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <div className="quiz-actions">
                  <button
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={!quiz.isNearby}
                    aria-disabled={!quiz.isNearby}
                  >
                    {quiz.isNearby ? t('quiz.answer') : t('quiz.moveCloser')}
                  </button>
                  <button className="clear-btn" onClick={clearInput}>
                    {t('quiz.clear')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}